import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Upload, ArrowLeft, FileSpreadsheet, CheckCircle, AlertCircle, PlusCircle, AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import ManualPickingListDialog from "@/components/picking/ManualPickingListDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [listName, setListName] = useState("");
  const [file, setFile] = useState(null);
  const pickingMode = "order"; // Fixed to order mode
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [showSkippedDialog, setShowSkippedDialog] = useState(false);
  const [skippedProducts, setSkippedProducts] = useState([]);
  const [createdListId, setCreatedListId] = useState(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      setError(null);
    } else {
      setError("Endast CSV-filer accepteras");
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Välj en CSV-fil först");
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const name = listName.trim() || `Plocklista ${new Date().toLocaleDateString('sv-SE')}`;
      const response = await axios.post(
        `${API}/picking/import-csv?list_name=${encodeURIComponent(name)}&picking_mode=${pickingMode}`, 
        formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const filteredItems = response.data.filtered_items || [];
      
      if (filteredItems.length > 0) {
        setSkippedProducts(filteredItems);
        setCreatedListId(response.data.id);
        setShowSkippedDialog(true);
        toast.success(response.data.message || `Plocklista skapad med ${response.data.total_items} produkter`);
      } else {
        toast.success(response.data.message || `Plocklista skapad med ${response.data.total_items} produkter`);
        navigate('/picking');
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : "Kunde inte importera filen";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleManualListCreated = (listId) => {
    navigate('/picking');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/picking">
              <Button variant="ghost" size="icon" data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight" data-testid="import-title">
              Skapa plocklista
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
        {/* Manual Creation Option */}
        <Card className="animate-slide-in border-primary/30 bg-primary/5" data-testid="manual-create-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10">
                  <PlusCircle className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold uppercase">Skapa manuellt</h3>
                  <p className="text-sm text-muted-foreground">
                    Sök och välj produkter direkt från lagret
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => setShowManualDialog(true)}
                className="gap-2"
                data-testid="open-manual-dialog-btn"
              >
                <PlusCircle className="h-4 w-4" />
                Skapa manuellt
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">eller importera CSV</span>
          </div>
        </div>

        {/* CSV Import */}
        <Card className="animate-slide-in" data-testid="import-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase tracking-tight">
              Ladda upp CSV-fil
            </CardTitle>
            <CardDescription>
              CSV-filen ska innehålla kolumnerna: Bring/DSV, Ordernummer, Produkt antal, Artikelnummer, Produktnamn, Produkt intern kommentar, Lagerplats, Produktstatus
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* List Name */}
            <div className="space-y-2">
              <Label htmlFor="listName">Namn på plocklista (valfritt)</Label>
              <Input
                id="listName"
                placeholder="T.ex. Morgonplockning 2024-01-15"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                data-testid="list-name-input"
              />
            </div>

            {/* Drop Zone */}
            <div
              className={`drop-zone ${dragOver ? 'drag-over' : ''} ${file ? 'border-success' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="drop-zone"
            >
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              
              {file ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 rounded-full bg-success/10">
                    <CheckCircle className="h-8 w-8 text-success" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                    Byt fil
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 rounded-full bg-muted">
                    <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      Dra och släpp CSV-fil här
                    </p>
                    <p className="text-sm text-muted-foreground">
                      eller klicka för att välja fil
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 text-destructive text-sm" data-testid="error-message">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            {/* CSV Format Info */}
            <div className="p-4 bg-muted/50 rounded-md space-y-2">
              <p className="text-sm font-medium">Förväntade kolumner (separerade med semikolon):</p>
              <code className="text-xs font-mono text-muted-foreground block">
                Bring/DSV;Ordernummer;Produkt antal;Artikelnummer;Produktnamn;Produkt intern kommentar;Lagerplats;Produktstatus
              </code>
              <div className="text-xs text-muted-foreground mt-2 space-y-1">
                <p><span className="text-foreground font-medium">Bring/DSV:</span> 8 = Bring, 42 = DSV</p>
                <p><span className="text-foreground font-medium">Produktstatus:</span> Endast "Redo för Plock" inkluderas</p>
              </div>
            </div>

            {/* Upload Button */}
            <Button
              className="big-button"
              onClick={handleUpload}
              disabled={!file || uploading}
              data-testid="upload-btn"
            >
              {uploading ? (
                "Importerar..."
              ) : (
                <>
                  <Upload className="h-5 w-5 mr-2" />
                  Skapa plocklista
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </main>

      {/* Manual Picking List Dialog */}
      <ManualPickingListDialog
        open={showManualDialog}
        onOpenChange={setShowManualDialog}
        onCreated={handleManualListCreated}
      />

      {/* Skipped Products Dialog */}
      <Dialog open={showSkippedDialog} onOpenChange={setShowSkippedDialog}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Filtrerade produkter ({skippedProducts.length})
            </DialogTitle>
            <DialogDescription>
              Dessa produkter togs bort fran plocklistan.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[50vh]">
            <div className="space-y-1.5 pr-3">
              {(() => {
                const noStock = skippedProducts.filter(p => p.reason === "no_stock" || p.reason === "not_in_system");
                const notAvail = skippedProducts.filter(p => p.reason === "not_available");
                return (
                  <>
                    {noStock.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-red-500 mb-2 flex items-center gap-2 text-sm">
                          <AlertCircle className="h-4 w-4" />
                          Saknar lager / ej i systemet ({noStock.length} st)
                        </h4>
                        <div className="space-y-1">
                          {noStock.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1.5 px-2 rounded bg-red-500/5 text-sm" data-testid={`filtered-no-stock-${idx}`}>
                              <div className="min-w-0">
                                <span className="font-mono font-medium">{item.artikelnummer || item.ean}</span>
                                {item.ordernummer && <span className="text-muted-foreground ml-2">#{item.ordernummer}</span>}
                              </div>
                              <span className="text-xs text-red-400 shrink-0 ml-2">{item.reason_text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {notAvail.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-amber-500 mb-2 flex items-center gap-2 text-sm">
                          <AlertTriangle className="h-4 w-4" />
                          Ej tillgangliga — allt bokat ({notAvail.length} st)
                        </h4>
                        <div className="space-y-1">
                          {notAvail.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1.5 px-2 rounded bg-amber-500/5 text-sm" data-testid={`filtered-not-avail-${idx}`}>
                              <div className="min-w-0">
                                <span className="font-mono font-medium">{item.artikelnummer || item.ean}</span>
                                {item.ordernummer && <span className="text-muted-foreground ml-2">#{item.ordernummer}</span>}
                              </div>
                              <span className="text-xs text-amber-400 shrink-0 ml-2">{item.reason_text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </ScrollArea>
          
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            {createdListId ? (
              <>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowSkippedDialog(false);
                    setCreatedListId(null);
                    setSkippedProducts([]);
                  }}
                >
                  Stang
                </Button>
                <Button onClick={() => {
                  setShowSkippedDialog(false);
                  navigate('/picking');
                }}>
                  Ga till plocklistor
                </Button>
              </>
            ) : (
              <Button 
                onClick={() => {
                  setShowSkippedDialog(false);
                  setSkippedProducts([]);
                }}
              >
                Stang
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
